import { X, History, RotateCcw, Trash2, Camera } from 'lucide-react';
import { useState } from 'react';
import {
  useDeckSnapshots,
  useCreateDeckSnapshot,
  useDeleteDeckSnapshot,
  useRestoreDeckSnapshot,
} from '@/hooks/useDeckSnapshots';
import type { DeckCard } from '@/hooks/useDecks';
import { toast } from 'sonner';

interface DeckHistorySheetProps {
  open: boolean;
  onClose: () => void;
  deckId: string;
  cards: DeckCard[];
}

export function DeckHistorySheet({ open, onClose, deckId, cards }: DeckHistorySheetProps) {
  const { data: snapshots, isLoading } = useDeckSnapshots(open ? deckId : null);
  const create = useCreateDeckSnapshot();
  const remove = useDeleteDeckSnapshot();
  const restore = useRestoreDeckSnapshot();
  const [label, setLabel] = useState('');

  if (!open) return null;

  const handleSnap = async () => {
    try {
      await create.mutateAsync({ deckId, label: label.trim() || 'Snapshot', cards });
      setLabel('');
      toast.success('Snapshot saved');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleRestore = async (id: string, snapshotLabel: string) => {
    const snap = snapshots?.find(s => s.id === id);
    if (!snap) return;
    if (!confirm(`Restore "${snapshotLabel}"? This replaces your current deck cards.`)) return;
    try {
      // Auto-snapshot current state before restoring so users can undo a restore.
      await create.mutateAsync({ deckId, label: 'Before restore', cards });
      await restore.mutateAsync({ deckId, payload: snap.snapshot_json });
      toast.success('Deck restored');
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await remove.mutateAsync({ id, deckId });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-slide-up w-full max-w-[430px] max-h-[90vh] flex flex-col rounded-t-2xl border border-border bg-card"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            <h3 className="font-display text-base font-semibold text-foreground">Deck History</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b border-border space-y-2">
          <p className="text-[11px] text-muted-foreground font-body">
            Snapshots keep a copy of your deck's exact card list so you can roll back. Auto-saved before imports and restores; latest 20 kept.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Label (optional)"
              className="flex-1 rounded-lg border border-border bg-secondary px-3 py-2 text-xs text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={handleSnap}
              disabled={create.isPending || cards.length === 0}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground font-body disabled:opacity-50"
            >
              <Camera className="h-3.5 w-3.5" /> {create.isPending ? 'Saving…' : 'Snap'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {isLoading ? (
            <div className="py-8 text-center text-xs text-muted-foreground font-body">Loading…</div>
          ) : !snapshots?.length ? (
            <div className="py-8 text-center">
              <p className="text-2xl mb-1.5">📸</p>
              <p className="text-xs text-muted-foreground font-body">No snapshots yet</p>
            </div>
          ) : (
            snapshots.map(s => {
              const total = (s.snapshot_json?.cards || []).reduce((sum, c) => sum + c.quantity, 0);
              const when = new Date(s.created_at);
              return (
                <div
                  key={s.id}
                  className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 p-2.5 animate-fade-in"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground font-body truncate">{s.label}</p>
                    <p className="text-[10px] text-muted-foreground font-body">
                      {total} cards · {when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRestore(s.id, s.label)}
                    disabled={restore.isPending}
                    className="flex items-center gap-1 rounded-lg bg-primary/10 px-2.5 py-1.5 text-[10px] font-medium text-primary font-body hover:bg-primary/20 disabled:opacity-50"
                  >
                    <RotateCcw className="h-3 w-3" /> Restore
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary text-muted-foreground hover:text-ink-ruby"
                    aria-label="Delete snapshot"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
